import React, { createContext, useCallback, useContext, useMemo, useRef, ReactNode } from 'react'
import { NavigationBlockerContextType, NavigationBlockerFn } from './interface'

const NavigationBlockerContext = createContext<NavigationBlockerContextType>({
  registerBlocker: () => {},
  runBlockers: async () => true,
})

export const NavigationBlockerProvider = ({ children }: { children: ReactNode }) => {
  const blockerRef = useRef<NavigationBlockerFn | null>(null)

  const registerBlocker = useCallback((blocker: NavigationBlockerFn | null) => {
    blockerRef.current = blocker
  }, [])

  const runBlockers = useCallback(async () => {
    if (!blockerRef.current) {
      return true
    }

    return blockerRef.current()
  }, [])

  const value = useMemo(
    () => ({
      registerBlocker,
      runBlockers,
    }),
    [registerBlocker, runBlockers],
  )

  return (
    <NavigationBlockerContext.Provider value={value}>{children}</NavigationBlockerContext.Provider>
  )
}

export const useNavigationBlockerContext = () => useContext(NavigationBlockerContext)

export const useNavigationBlocker = (enabled: boolean, blocker: NavigationBlockerFn) => {
  const { registerBlocker } = useNavigationBlockerContext()
  const blockerRef = useRef(blocker)
  blockerRef.current = blocker

  React.useEffect(() => {
    if (!enabled) {
      registerBlocker(null)
      return
    }

    registerBlocker(() => blockerRef.current())

    return () => {
      registerBlocker(null)
    }
  }, [enabled, registerBlocker])
}
